import type {
  CollectionReference,
  DatabaseService,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  QueryConstraint,
} from "@infrastructure/interfaces/database.interface";
import logger from "@logger";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../config/firebase";

export class FirebaseDatabaseService implements DatabaseService {
  /**
   * Converts Firebase timestamps to JavaScript Date objects
   * Recursively processes objects to handle nested timestamps
   */
  private convertTimestampsToDates<T>(data: unknown): T {
    if (data === null || data === undefined) {
      return data as T;
    }

    if (data instanceof Timestamp) {
      return data.toDate() as T;
    }

    if (Array.isArray(data)) {
      return data.map((item) => this.convertTimestampsToDates(item)) as T;
    }

    if (typeof data === "object") {
      const converted: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        converted[key] = this.convertTimestampsToDates(value);
      }
      return converted as T;
    }

    return data as T;
  }

  // Generic CRUD operations
  async getDocument<T = DocumentData>(collectionPath: string, documentId: string): Promise<T | null> {
    try {
      const docRef = doc(db, collectionPath, documentId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const rawData = { id: docSnap.id, ...docSnap.data() };
        return this.convertTimestampsToDates<T>(rawData);
      }
      return null;
    } catch (error) {
      logger.error("Error getting document:", error);
      throw error;
    }
  }

  async getDocuments<T = DocumentData>(collectionPath: string, constraints: QueryConstraint[] = []): Promise<T[]> {
    try {
      const collectionRef = collection(db, collectionPath);
      const q = query(collectionRef, ...constraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const rawData = { id: doc.id, ...doc.data() };
        return this.convertTimestampsToDates<T>(rawData);
      });
    } catch (error) {
      logger.error("Error getting documents:", error);
      throw error;
    }
  }

  async addDocument<T = DocumentData>(collectionPath: string, data: Omit<T, "id">): Promise<string> {
    try {
      const collectionRef = collection(db, collectionPath);
      const docRef = await addDoc(collectionRef, {
        ...data,
        created_time: serverTimestamp(),
        updated_time: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      logger.error("Error adding document:", error);
      throw error;
    }
  }

  async setDocument<T = DocumentData>(
    collectionPath: string,
    documentId: string,
    data: Omit<T, "id">,
    merge = false
  ): Promise<void> {
    try {
      const docRef = doc(db, collectionPath, documentId);
      await setDoc(docRef, data, { merge });
    } catch (error) {
      logger.error("Error setting document:", error);
      throw error;
    }
  }

  async updateDocument<T = DocumentData>(collectionPath: string, documentId: string, data: Partial<T>): Promise<void> {
    try {
      const docRef = doc(db, collectionPath, documentId);
      await updateDoc(docRef, {
        ...data,
        updated_time: serverTimestamp(),
      });
    } catch (error) {
      logger.error("Error updating document:", error);
      throw error;
    }
  }

  async deleteDocument(collectionPath: string, documentId: string): Promise<void> {
    try {
      const docRef = doc(db, collectionPath, documentId);
      await deleteDoc(docRef);
    } catch (error) {
      logger.error("Error deleting document:", error);
      throw error;
    }
  }

  // Real-time subscriptions
  subscribeToDocument<T = DocumentData>(
    collectionPath: string,
    documentId: string,
    callback: (data: T | null) => void,
    onError?: (error: Error) => void
  ): () => void {
    const docRef = doc(db, collectionPath, documentId);

    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const rawData = { id: docSnap.id, ...docSnap.data() };
          const data = this.convertTimestampsToDates<T>(rawData);
          callback(data);
        } else {
          callback(null);
        }
      },
      (error) => {
        logger.error("Firestore document subscription failed", {
          collectionPath,
          documentId,
          code: "code" in error ? error.code : undefined,
          hasAuthUser: Boolean(auth.currentUser),
          message: error.message,
        });
        onError?.(error);
      }
    );
  }

  subscribeToCollection<T = DocumentData>(
    collectionPath: string,
    callback: (data: T[]) => void,
    constraints: QueryConstraint[] = [],
    onError?: (error: Error) => void
  ): () => void {
    const collectionRef = collection(db, collectionPath);
    const q = query(collectionRef, ...constraints);

    return onSnapshot(
      q,
      (querySnapshot) => {
        const data = querySnapshot.docs.map((docSnap) => {
          const rawData = { id: docSnap.id, ...docSnap.data() };
          return this.convertTimestampsToDates<T>(rawData);
        });
        callback(data);
      },
      (error) => {
        logger.error("Firestore collection subscription failed", {
          collectionPath,
          constraintCount: constraints.length,
          code: "code" in error ? error.code : undefined,
          hasAuthUser: Boolean(auth.currentUser),
          message: error.message,
        });
        onError?.(error);
      }
    );
  }

  // Query builders
  createQuery(collectionPath: string, constraints: QueryConstraint[] = []): unknown {
    const collectionRef = collection(db, collectionPath);
    return query(collectionRef, ...constraints);
  }

  // Utility methods
  getCollectionReference(collectionPath: string): CollectionReference {
    return collection(db, collectionPath);
  }

  getDocumentReference(collectionPath: string, documentId: string): DocumentReference {
    return doc(db, collectionPath, documentId);
  }

  // Pagination helpers
  async getPaginatedDocuments<T = DocumentData>(
    collectionPath: string,
    constraints: QueryConstraint[] = [],
    pageSize: number = 20,
    lastDoc?: DocumentSnapshot<unknown>
  ): Promise<{ data: T[]; lastDoc: DocumentSnapshot<unknown> | null }> {
    try {
      const queryConstraints = [...constraints, limit(pageSize)];

      if (lastDoc) {
        queryConstraints.push(startAfter(lastDoc));
      }

      const data = await this.getDocuments<T>(collectionPath, queryConstraints);
      const lastVisible =
        data.length > 0
          ? await this.getDocumentSnapshot(collectionPath, (data[data.length - 1] as T & { id: string }).id)
          : null;

      return { data, lastDoc: lastVisible };
    } catch (error) {
      logger.error("Error getting paginated documents:", error);
      throw error;
    }
  }

  private async getDocumentSnapshot(collectionPath: string, documentId: string): Promise<DocumentSnapshot | null> {
    try {
      const docRef = doc(db, collectionPath, documentId);
      return await getDoc(docRef);
    } catch (error) {
      logger.error("Error getting document snapshot:", error);
      return null;
    }
  }

  /**
   * Get Firestore instance for transactions
   */
  getFirestoreInstance() {
    return db;
  }

  /**
   * Run a Firestore transaction
   */
  async runTransaction<T>(updateFunction: (transaction: unknown) => Promise<T>): Promise<T> {
    return await runTransaction(db, updateFunction);
  }
}

// Export singleton instance
export const firebaseDatabaseService = new FirebaseDatabaseService();
export default firebaseDatabaseService;
